import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, ChevronDown, CalendarClock } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, formatVND, formatCompact } from '@/lib/utils';
import { fmtMoney } from '../../components/kit/format';
import { duplicateTemplateIds } from '../../../finance';
import { useCoreReady, useData } from '../../store/data';
import Money from '../../components/kit/Money';
import Pill from '../../components/kit/Pill';
import SpendTotals from '../../components/kit/SpendTotals';
import LeafHeader from '../../components/kit/LeafHeader';
import RecurringTab from './RecurringTab';
import { ConfirmSheet, EditSheet, SkipSheet, AddSheet } from './Sheets';
import { DayList, type Handlers } from './DayList';
import { plannedForMonths, groupPlanned, summarizePlannedIn, duplicatePayments, splitUpcoming, groupNet, type PlannedEntry, type PlannedKind } from './plannedEntries';
import { toast } from 'sonner';
import { getAuth } from 'firebase/auth';
import { confirmAsk } from '../../components/confirm';
import { materializeFromTemplate, discardUntouched } from '../../../ledger/plans';
import { reopenSkipped, undoDialog, runUndo } from './manage';
import { currentPeriod, stepPeriod, switchMode, periodFilter, periodMonths, type Period, type PeriodMode } from '../transactions/period';

type Tab = 'items' | 'templates';
type Filter = 'all' | PlannedKind;
const FILTERS: Array<{ id: Filter; label: string }> = [{ id: 'all', label: 'Tất cả' }, { id: 'income', label: 'Thu' }, { id: 'expense', label: 'Chi' }, { id: 'payment', label: 'Trả thẻ & nợ' }];
function Group({ title, hint, items, numeral, h }: { title: string; hint?: string; items: PlannedEntry[]; numeral: string; h: Handlers }) {
  if (items.length === 0) return null;
  return (
    <section className="space-y-2" aria-label={title}>
      <h2 className="flex flex-wrap items-baseline gap-x-2 px-1 text-base font-semibold">{title}
        <span className="num text-sm font-normal text-muted-foreground">{items.length} khoản · {fmtMoney(groupNet(items), { compact: true })}</span>
        {hint && <span className="text-sm font-normal text-muted-foreground">{hint}</span>}</h2>
      <DayList items={items} numeral={numeral} h={h} />
    </section>
  );
}

function Collapsible({ title, items, h }: { title: string; items: PlannedEntry[]; h: Handlers }) {
  const [open, setOpen] = useState(false);
  if (items.length === 0) return null;
  return (
    <section className="space-y-2">
      <button onClick={() => setOpen(o => !o)} aria-expanded={open} className="flex min-h-9 items-center gap-1 px-1 text-base font-semibold hover:text-primary">
        <ChevronDown size={16} className={cn('transition-transform motion-reduce:transition-none', !open && '-rotate-90')} aria-hidden /> {title} <span className="num font-normal text-muted-foreground">{items.length}</span>
      </button>
      {open && <DayList items={items} numeral="text-muted-foreground" h={h} />}
    </section>
  );
}

export default function Planned() {
  const ready = useCoreReady();
  const data = useData(s => s.data);
  const now = new Date();
  const [tab, setTab] = useState<Tab>('items');
  const [period, setPeriod] = useState<Period>(() => currentPeriod(now));
  const months = useMemo(() => periodMonths(period), [period]);
  const { year, month, quarter } = periodFilter(period);
  const whenLabel = period.mode === 'month' ? `tháng ${period.month}/${period.year}` : period.mode === 'quarter' ? `quý ${period.quarter}/${period.year}` : `năm ${period.year}`;
  const [confirm, setConfirm] = useState<PlannedEntry | null>(null);
  const [edit, setEdit] = useState<PlannedEntry | null>(null);
  const [skip, setSkip] = useState<PlannedEntry | null>(null);
  const [adding, setAdding] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');

  const entries = useMemo(
    () => plannedForMonths({ plannedExpenses: data.plannedExpenses, plannedIncomes: data.plannedIncomes, plannedPayments: data.plannedPayments, recurringTemplates: data.recurringTemplates }, months),
    [data.plannedExpenses, data.plannedIncomes, data.plannedPayments, data.recurringTemplates, months]
  );
  const shown = filter === 'all' ? entries : entries.filter(e => e.kind === filter);
  const counts = (id: Filter) => (id === 'all' ? entries : entries.filter(e => e.kind === id)).length;
  const dupCount = duplicateTemplateIds(data.recurringTemplates).length;
  const dupPayments = useMemo(() => duplicatePayments(entries), [entries]);
  const groups = groupPlanned(shown);
  const upcoming = splitUpcoming(groups.upcoming, now);
  const cardIds = useMemo(() => new Set<string>(data.sources.filter((x: any) => x.type === 'credit').map((x: any) => x.id)), [data.sources]);
  const sum = summarizePlannedIn(entries, months, cardIds);
  const step = (n: number) => setPeriod(p => stepPeriod(p, n));
  const isCurrent = months.some(x => x.y === now.getFullYear() && x.m === now.getMonth() + 1);
  const pct = sum.total > 0 ? Math.round((sum.confirmed / sum.total) * 100) : 0;
  const uid = () => getAuth().currentUser!.uid;
  const onReopen = async (e: PlannedEntry) => {
    const r = await reopenSkipped(uid(), e);
    r.success ? toast.success('Đã mở lại — khoản đang chờ xác nhận') : toast.error(r.error ?? 'Không mở lại được');
  };
  const onUndo = async (e: PlannedEntry) => {
    const dlg = undoDialog(e);
    if ('blocked' in dlg) { toast.error(dlg.blocked, { duration: 8000 }); return; }
    if (!await confirmAsk(dlg)) return;
    const r = await runUndo(uid(), e);
    if (!r.success) { toast.error(r.error ?? 'Không hoàn tác được', { duration: 8000 }); return; }
    r.warnings?.forEach(w => toast.warning(w, { duration: 7000 }));
    toast.success('Đã hoàn tác — khoản trở về chờ xác nhận');
  };
  // Khoản "Sẽ được tạo": tạo bản ghi từ mẫu rồi mở phiếu xác nhận ngay khi nó xuất hiện trong dữ liệu
  // Khoản vừa được tạo từ mẫu chỉ để mở hộp: đóng hộp mà chưa làm gì thì xoá lại, khỏi để bản ghi chờ thừa
  const fresh = useRef(new Map<string, PlannedEntry['kind']>());
  const closeSheet = (setter: (e: null) => void, e: PlannedEntry) => () => {
    setter(null);
    const kind = fresh.current.get(e.id);
    if (!kind) return;
    fresh.current.delete(e.id);
    discardUntouched(uid(), kind === 'income' ? 'plannedIncomes' : kind === 'payment' ? 'plannedPayments' : 'plannedExpenses', e.id);
  };
  const [awaiting, setAwaiting] = useState<{ id: string; to: 'confirm' | 'edit' | 'skip' } | null>(null);
  useEffect(() => {
    if (!awaiting) return;
    const real = entries.find(x => x.id === awaiting.id && !x.virtual);
    if (!real) return;
    setAwaiting(null);
    (awaiting.to === 'edit' ? setEdit : awaiting.to === 'skip' ? setSkip : setConfirm)(real);
  }, [awaiting, entries]);
  const viaTemplate = (to: 'confirm' | 'edit' | 'skip', direct: (e: PlannedEntry) => void) => async (e: PlannedEntry) => {
    if (!e.virtual) { direct(e); return; }
    const r = await materializeFromTemplate(uid(), e.doc, e.date!.getFullYear(), e.date!.getMonth() + 1);
    if (!r.success) { toast.error(r.error ?? 'Không tạo được khoản từ mẫu'); return; }
    fresh.current.set(e.id, e.kind);
    setAwaiting({ id: e.id, to });
  };
  const h: Handlers = { onConfirm: viaTemplate('confirm', setConfirm), onEdit: viaTemplate('edit', setEdit), onSkip: viaTemplate('skip', setSkip), onReopen, onUndo };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-slab text-3xl font-bold tracking-tight">Dự kiến</h1>
          <p className="mt-1 max-w-prose text-muted-foreground">Theo dõi các khoản thu, chi, trả thẻ & nợ của tháng này. Đến ngày, bấm Xác nhận để ghi sổ ngay: ví, dư nợ thẻ và khoản vay, danh sách giao dịch, báo cáo và Tổng quan đều cập nhật theo.</p>
        </div>
        <Button onClick={() => setAdding(true)}><Plus size={16} aria-hidden /> Thêm khoản dự kiến</Button>
      </div>

      <Tabs value={tab} onValueChange={v => setTab(v as Tab)}>
        <TabsList className="grid w-full max-w-xs grid-cols-2"><TabsTrigger value="items">Khoản dự kiến</TabsTrigger><TabsTrigger value="templates">Mẫu định kỳ</TabsTrigger></TabsList>
      </Tabs>

      {dupCount > 0 && tab === 'items' && (
        <Card role="alert" className="flex flex-wrap items-center justify-between gap-3 border-warn/40 bg-warn-soft p-4">
          <p className="min-w-0 flex-1 basis-60 text-sm"><b className="text-warn">{dupCount} mẫu định kỳ bị trùng.</b> Các khoản tháng sau đang hiện hai lần và tổng bị gấp đôi. Hãy xoá bản thừa trước khi sang tháng mới.</p>
          <Button variant="outline" onClick={() => setTab('templates')}>Xem và xoá bản trùng</Button>
        </Card>
      )}

      {dupPayments.length > 0 && tab === 'items' && (
        <Card role="alert" className="space-y-1 border-warn/40 bg-warn-soft p-4 text-sm">
          <p className="font-medium text-warn">{dupPayments.length} thẻ hoặc khoản vay có nhiều khoản trả cùng tháng</p>
          <ul className="num text-muted-foreground">{dupPayments.map(d => <li key={d.key}>{d.label}: {d.entries.length} khoản, tổng {formatVND(d.total)} ({d.entries.map(e => formatCompact(e.amount)).join(' + ')}). Nếu chỉ phải trả một lần, hãy bỏ qua các khoản thừa.</li>)}</ul>
        </Card>
      )}

      {tab === 'templates' ? <RecurringTab /> : !ready ? <div className="space-y-3"><Skeleton className="h-40" /><Skeleton className="h-48" /></div> : (
        <>
          <section aria-label={`Tổng hợp ${whenLabel}`} className="leaf-curl overflow-hidden rounded-lg border border-border bg-card">
            <LeafHeader month={month} quarter={quarter} year={year} onStep={step} />
            <div className="space-y-4 p-4 sm:p-5">
              <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
                {[
                  { l: 'Dự thu', v: sum.income, tone: 'in' as const },
                  { l: 'Dự chi', v: sum.expense, tone: undefined },
                  { l: 'Trả thẻ & nợ', v: sum.payment, tone: undefined },
                  { l: 'Chênh lệch', v: sum.net, tone: undefined }
                ].map(x => (
                  <div key={x.l}>
                    <dt className="text-sm text-muted-foreground">{x.l}</dt>
                    <dd><Money value={x.v} size="figure" tone={x.tone} compact /></dd>
                  </div>
                ))}
              </dl>
              <SpendTotals planned={sum.spend} done={sum.spendDone} left={sum.spendLeft} doneLabel="Đã chi (đã xác nhận)" note={sum.cardSpend > 0 ? `Chưa gồm ${fmtMoney(sum.cardSpend, { compact: true })} dự chi quẹt thẻ — sẽ tính khi trả thẻ.` : undefined} />
              <div className="space-y-1.5">
                <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                  <span className="text-muted-foreground">Đã xác nhận <b className="num font-semibold text-foreground">{sum.confirmed}/{sum.total}</b> khoản</span>
                  {isCurrent && sum.lateCount > 0 && <Pill tone="warn">{sum.lateCount} khoản quá hạn{sum.late60Count > 0 ? `, ${sum.late60Count} khoản trên 60 ngày` : ''}</Pill>}
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-secondary" role="img" aria-label={`Đã xác nhận ${pct}%`}><div className="h-full rounded-full bg-primary transition-[width] duration-500 motion-reduce:transition-none" style={{ width: `${pct}%` }} /></div>
              </div>
              <Tabs value={period.mode} onValueChange={v => setPeriod(p => switchMode(p, v as PeriodMode))}>
                <TabsList><TabsTrigger value="month">Theo tháng</TabsTrigger><TabsTrigger value="quarter">Theo quý</TabsTrigger><TabsTrigger value="year">Cả năm</TabsTrigger></TabsList>
              </Tabs>
              <Link to="/v2/ke-hoach" className="inline-flex min-h-9 items-center gap-1.5 text-sm font-medium text-primary hover:underline"><CalendarClock size={14} aria-hidden /> Xem dự báo dòng tiền 30–60 ngày</Link>
            </div>
          </section>

          <div className="flex flex-wrap gap-2" role="group" aria-label="Lọc theo loại">
            {FILTERS.map(f => (
              <button key={f.id} onClick={() => setFilter(f.id)} aria-pressed={filter === f.id}
                className={cn('min-h-9 rounded-full border px-3.5 text-sm font-medium transition-colors', filter === f.id ? 'border-primary bg-accent text-accent-foreground' : 'border-border text-muted-foreground hover:bg-secondary')}>
                {f.label} <span className="num ml-0.5 font-normal opacity-70">{counts(f.id)}</span>
              </button>
            ))}
          </div>

          {entries.length > 0 && shown.length === 0 && (
            <Card className="p-6 text-center text-muted-foreground">Không có khoản "{FILTERS.find(f => f.id === filter)!.label}" nào trong {whenLabel}.</Card>
          )}
          {entries.length === 0 && (
            <Card className="space-y-3 p-8 text-center">
              <p className="font-slab text-lg font-semibold">Chưa có khoản dự kiến nào trong {whenLabel}</p>
              <p className="text-muted-foreground">Thêm khoản đầu tiên, hoặc tạo mẫu định kỳ để khoản lặp lại tự xuất hiện mỗi tháng.</p>
              <Button onClick={() => setAdding(true)}><Plus size={16} aria-hidden /> Thêm khoản dự kiến</Button>
            </Card>
          )}
          <Group title="Quá hạn" hint="chưa xác nhận" items={groups.late} numeral="text-bad" h={h} />
          <Group title={upcoming.soon.length > 0 && upcoming.rest.length > 0 ? 'Trong 7 ngày tới' : 'Sắp tới'} items={upcoming.soon} numeral="text-brand" h={h} />
          <Group title={upcoming.soon.length > 0 ? 'Sau đó' : 'Sắp tới'} items={upcoming.rest} numeral="text-brand" h={h} />
          <Group title="Chưa tạo, từ mẫu định kỳ" hint="Xác nhận, Sửa hay Bỏ qua sẽ tạo khoản đó" items={groups.later} numeral="text-brand/50" h={h} />
          <Collapsible title="Đã xác nhận" items={groups.done} h={h} />
          <Collapsible title="Đã bỏ qua" items={groups.skipped} h={h} />
        </>
      )}

      {confirm && <ConfirmSheet entry={confirm} onClose={closeSheet(setConfirm, confirm)} />}
      {edit && <EditSheet entry={edit} onClose={closeSheet(setEdit, edit)} />}
      {skip && <SkipSheet entry={skip} onClose={closeSheet(setSkip, skip)} />}
      {adding && <AddSheet onClose={() => setAdding(false)} />}
    </div>
  );
}
